// import React, {
//   createContext,
//   useCallback,
//   useContext,
//   useEffect,
//   useMemo,
// } from "react";
// import { useDispatch } from "react-redux";
// import { clearUser, setUser } from "../store/authSlice";
// import {
//   InteractionRequiredAuthError,
//   InteractionStatus,
//   AccountInfo,
//   EventType,
// } from "@azure/msal-browser";
// import { useIsAuthenticated, useMsal } from "@azure/msal-react";
// import { defaultMsalScopes } from "../AuthConfig";
// import {
//   clearCachedAccessToken,
//   setCachedAccessToken,
// } from "../utils/tokenStorage";

// type AuthContextValue = {
//   isAuthenticated: boolean;
//   inProgress: InteractionStatus;
//   account: AccountInfo | null;
//   login: () => Promise<void>;
//   logout: () => Promise<void>;
//   getAccessToken: (scopes?: string[]) => Promise<string | null>;
// };
// const DEFAULT_SCOPES = defaultMsalScopes.length ? defaultMsalScopes : ["User.Read"];

// const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// export const AuthProvider: React.FC<React.PropsWithChildren> = ({
//   children,
// }) => {
//   const dispatch = useDispatch();
//   const { instance, accounts, inProgress } = useMsal();
//   const isAuthenticated = useIsAuthenticated();
//   const account = useMemo(() => {
//     return instance.getActiveAccount() ?? accounts?.[0] ?? null;
//   }, [accounts, instance]);

//   useEffect(() => {
//     if (accounts.length && !instance.getActiveAccount()) {
//       instance.setActiveAccount(accounts[0]);
//     }
//   }, [accounts, instance]);

//   // Keep Redux UI pieces working without changing UI components
//   useEffect(() => {
//     if (account) {
//       dispatch(setUser(account));
//     } else {
//       dispatch(clearUser());
//       // Clear token when no account
//       clearCachedAccessToken();
//     }
//   }, [account, dispatch]);

//   // Acquire token on initial auth to populate localStorage
//   useEffect(() => {
//     if (account && isAuthenticated) {
//       const acquireToken = async () => {
//         try {
//           const req = {
//             account: account,
//             scopes: DEFAULT_SCOPES,
//           };
//           const result = await instance.acquireTokenSilent(req);
//           if (result?.accessToken) {
//             setCachedAccessToken(result.accessToken);
//           }
//         } catch (err) {
//           // Initial token acquisition error handled by MSAL
//         }
//       };
//       acquireToken();
//     }
//   }, [account, isAuthenticated, instance]);

//   const login = useCallback(async () => {
//     await instance.loginRedirect({
//       scopes: DEFAULT_SCOPES,
//       redirectStartPage: window.location.pathname + window.location.search,
//     });
//   }, [instance]);

//   const logout = useCallback(async () => {
//     // Mark a logout so guards won't auto-login on the next navigation
//     try {
//       sessionStorage.setItem("justLoggedOut", "1");
//     } catch {}
//     clearCachedAccessToken(); // Clear token on logout
//     // Use popup to avoid full-page roundtrip
//     await instance.logoutPopup({ postLogoutRedirectUri: "/" });
//   }, [instance]);

//   const getAccessToken = useCallback(
//     async (scopes?: string[]) => {
//       const req = {
//         account: account ?? undefined,
//         scopes: scopes ?? DEFAULT_SCOPES,
//       };
//       try {
//         const result = await instance.acquireTokenSilent(req);
//         const token = result?.accessToken ?? null;

//         // Store token so Axios interceptors can reuse it after reloads
//         if (token) {
//           setCachedAccessToken(token);
//         }

//         return token;
//       } catch (err) {
//         if (err instanceof InteractionRequiredAuthError) {
//           await instance.acquireTokenRedirect(req);
//           return null;
//         }
//         return null;
//       }
//     },
//     [instance, account]
//   );

//   const value = useMemo<AuthContextValue>(
//     () => ({
//       isAuthenticated,
//       inProgress,
//       account,
//       login,
//       logout,
//       getAccessToken,
//     }),
//     [isAuthenticated, inProgress, account, login, logout, getAccessToken]
//   );

//   // React to MSAL events for login/logout to keep flags/accounts in sync
//   useEffect(() => {
//     const cbId = instance.addEventCallback((event) => {
//       if (
//         event.eventType === EventType.LOGIN_SUCCESS &&
//         event.payload?.account
//       ) {
//         instance.setActiveAccount(event.payload.account);
//       }
//       if (event.eventType === EventType.LOGOUT_SUCCESS) {
//         try {
//           sessionStorage.setItem("justLoggedOut", "1");
//         } catch {}
//         clearCachedAccessToken();
//       }
//     });
//     return () => {
//       if (cbId) instance.removeEventCallback(cbId);
//     };
//   }, [instance]);

//   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
// };

// export const useAuth = (): AuthContextValue => {
//   const ctx = useContext(AuthContext);
//   if (!ctx) throw new Error("useAuth must be used within AuthProvider");
//   return ctx;
// };

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { clearUser, setUser } from "../store/authSlice";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  AccountInfo,
  EventType,
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { defaultMsalScopes } from "../AuthConfig";
import {
  clearCachedAccessToken,
  setCachedAccessToken,
} from "../utils/tokenStorage";

type AuthContextValue = {
  isAuthenticated: boolean;
  inProgress: InteractionStatus;
  account: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: (scopes?: string[]) => Promise<string | null>;
};

const DEFAULT_SCOPES = defaultMsalScopes.length
  ? defaultMsalScopes
  : ["User.Read"];

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account = useMemo(() => {
    return instance.getActiveAccount() ?? accounts?.[0] ?? null;
  }, [accounts, instance]);

  /* ---------------- set active account ---------------- */

  useEffect(() => {
    if (accounts.length && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  /* ---------------- sync redux user ---------------- */

  useEffect(() => {
    if (account) {
      dispatch(setUser(account));
    } else {
      dispatch(clearUser());
      clearCachedAccessToken();
    }
  }, [account, dispatch]);

  /* ---------------- initial token acquisition ---------------- */

  useEffect(() => {
    if (account && isAuthenticated) {
      const acquireToken = async () => {
        try {
          const req = {
            account,
            scopes: DEFAULT_SCOPES,
          };
          const result = await instance.acquireTokenSilent(req);
          if (result?.accessToken) {
            setCachedAccessToken(result.accessToken);
          }
        } catch {
          // handled by MSAL
        }
      };
      acquireToken();
    }
  }, [account, isAuthenticated, instance]);

  /* ---------------- login ---------------- */

  const login = useCallback(async () => {
    await instance.loginRedirect({
      scopes: DEFAULT_SCOPES,
      redirectStartPage: window.location.pathname + window.location.search,
    });
  }, [instance]);

  /* ---------------- logout ---------------- */

  const logout = useCallback(async () => {
    try {
      sessionStorage.setItem("justLoggedOut", "1");
    } catch { }
    clearCachedAccessToken();
    await instance.logoutPopup({ postLogoutRedirectUri: "/" });
  }, [instance]);

  /* ---------------- get access token ---------------- */

  const getAccessToken = useCallback(
    async (scopes?: string[]) => {
      const req = {
        account: account ?? undefined,
        scopes: scopes ?? DEFAULT_SCOPES,
      };

      try {
        const result = await instance.acquireTokenSilent(req);
        const token = result?.accessToken ?? null;

        if (token) {
          setCachedAccessToken(token);
        }

        return token;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect(req);
        }
        return null;
      }
    },
    [instance, account]
  );

  /* ---------------- CONTEXT VALUE ---------------- */

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      inProgress,
      account,
      login,
      logout,
      getAccessToken,
    }),
    [isAuthenticated, inProgress, account, login, logout, getAccessToken]
  );

  /* =========================================================
     PROACTIVE TOKEN REFRESH (EVERY 30 MINUTES)
     ========================================================= */

  useEffect(() => {
    if (!account || !isAuthenticated) return;

    let intervalId: number;

    const refreshTokenSilently = async () => {
      try {
        const req = {
          account,
          scopes: DEFAULT_SCOPES,
        };

        const result = await instance.acquireTokenSilent(req);
        if (result?.accessToken) {
          setCachedAccessToken(result.accessToken);
          console.log("Access token refreshed (30-min interval)");
        }
      } catch (err) {
        console.warn("Silent token refresh failed", err);
        clearCachedAccessToken();
        try {
          instance.setActiveAccount(null);
          await instance.clearCache();
        } catch { }
        console.log("Triggering re-login due to token refresh failure");
        await instance.loginRedirect({
          scopes: DEFAULT_SCOPES,
          loginHint: account.username,
          redirectStartPage: window.location.href,
        });
      }
    };

    // Refresh once immediately
    refreshTokenSilently();

    // Refresh every 30 minutes
    intervalId = window.setInterval(
      refreshTokenSilently,
      30 * 60 * 1000
    );

    return () => clearInterval(intervalId);
  }, [account, isAuthenticated, instance]);

  /* ---------------- MSAL event handling ---------------- */

  useEffect(() => {
    const cbId = instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload?.account
      ) {
        instance.setActiveAccount(event.payload.account);
      }

      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        try {
          sessionStorage.setItem("justLoggedOut", "1");
        } catch { }
        clearCachedAccessToken();
      }
    });

    return () => {
      if (cbId) instance.removeEventCallback(cbId);
    };
  }, [instance]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
